import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import RightTimeApi from "@/API";
import Modal from "@modalSrc/Modal";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import Enums from "@/enums";
import { useI18n } from "@/i18n/I18nProvider";

const buildDefaultSite = (company) => ({
    siteId: 0,
    siteName: "",
    siteAlias: "",
    company: company || null,
    companyName: "",
    siteHasImage: false,
    jobs: [],
    activeJobCount: 0,
    totalJobCount: 0
});

const EMPTY_DEFAULT_JOB_IDS = [];

export default function EditSiteModal({
    isOpen,
    onClose,
    siteId,
    updateSpinner,
    jobs,
    company,
    adminPerms,
    defaultJobIds = EMPTY_DEFAULT_JOB_IDS
}) {
    const [site, setSite] = useState(null);
    const [formSite, setFormSite] = useState(null);
    const [originalSite, setOriginalSite] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [companyTestTypes, setCompanyTestTypes] = useState([]);
    const [selectedCttIds, setSelectedCttIds] = useState([]);
    const { t } = useI18n();

    useEffect(() => {
        if (!isOpen) {
            setSite(null);
            setIsLoading(false);
            return;
        }

        let isMounted = true;

        async function loadSite() {
            if (!siteId || siteId <= 0) {
                const initialJobIds = Array.isArray(defaultJobIds)
                    ? defaultJobIds.filter((id) => Number.isFinite(Number(id)) && Number(id) > 0).map((id) => Number(id))
                    : [];
                const emptySite = { ...buildDefaultSite(company), jobs: initialJobIds };
                setSite(emptySite);
                setFormSite(emptySite);
                setOriginalSite(emptySite);
                setIsLoading(false);
                return;
            }

            if (!company.id) {
                toast.error(t("Company id is required to load site details."));
                const emptySite = buildDefaultSite(company);
                setSite(emptySite);
                setFormSite(emptySite);
                setOriginalSite(emptySite);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setSite(null);
            if (typeof updateSpinner === "function") {
                updateSpinner(true);
            }

            const resp = await RightTimeApi.getAsync(
                `/Company/${company.id}/Site/${siteId}`
            );

            if (typeof updateSpinner === "function") {
                updateSpinner(false);
            }


            if (!isMounted) return;

            if (resp?.ok && resp.jsonData) {
                setSite(resp.jsonData);
                setFormSite(resp.jsonData);
                setOriginalSite(resp.jsonData);
                setSelectedCttIds(Array.isArray(resp.jsonData.cttIds) ? resp.jsonData.cttIds : []);
            } else {
                toast.error(t("Failed to load site details."));
                const emptySite = buildDefaultSite(company);
                setSite(emptySite);
                setFormSite(emptySite);
                setOriginalSite(emptySite);
                setSelectedCttIds([]);
            }
            if (adminPerms.includes(Enums.ROLE_PERMS.SystemAdmin)) {
                const resp2 = await RightTimeApi.getAsync(`/Company/${company.id}/CompanyTestTypes`);
                if (resp2?.ok && resp2.jsonData) {
                    setCompanyTestTypes(resp2.jsonData);
                }
            }


            setIsLoading(false);
        }

        loadSite();

        return () => {
            isMounted = false;
        };
    }, [isOpen, siteId, company.id, defaultJobIds.join(",")]);

    function handleNameChange(e) {
        const value = e.target.value;
        setFormSite((prev) => ({ ...prev, siteName: value }));
    }

    function handleAliasChange(e) {
        const value = e.target.value;
        setFormSite((prev) => ({ ...prev, siteAlias: value }));
    }

    function handleNameKeyDown(e) {
        if (e.key !== "Enter") return;
        e.preventDefault();
        if (!isChanged() || !formSite?.siteName?.trim()) return;
        handleSave();
    }

    function toggleJobAvailability(jobId) {
        setFormSite((prev) => {
            if (!prev) return prev;
            const jobIds = Array.isArray(prev.jobs) ? [...prev.jobs] : [];
            const hasJob = jobIds.includes(jobId);
            const nextJobs = hasJob
                ? jobIds.filter((id) => id !== jobId)
                : [...jobIds, jobId];
            return { ...prev, jobs: nextJobs };
        });
    }

    function toggleCttAvailability(cttId) {
        setSelectedCttIds((prev) => {
            if (prev.includes(cttId)) {
                return prev.filter((id) => id !== cttId);
            } else {
                return [...prev, cttId];
            }
        });
    }

    function isChanged() {
        if (!formSite || !originalSite) return false;
        if ((formSite.siteName || "") !== (originalSite.siteName || "")) return true;
        if ((formSite.siteAlias || "") !== (originalSite.siteAlias || "")) return true;
        const formJobs = Array.isArray(formSite.jobs) ? formSite.jobs : [];
        const originalJobs = Array.isArray(originalSite.jobs) ? originalSite.jobs : [];
        if (formJobs.length !== originalJobs.length) return true;
        const originalSet = new Set(originalJobs);
        for (let i = 0; i < formJobs.length; i += 1) {
            if (!originalSet.has(formJobs[i])) return true;
        }
        
        const originalCttIds = Array.isArray(originalSite.cttIds) ? originalSite.cttIds : [];
        const currentCttIds = Array.isArray(selectedCttIds) ? selectedCttIds : [];
        if (originalCttIds.length !== currentCttIds.length) return true;
        const originalCttSet = new Set(originalCttIds);
        for (let i = 0; i < currentCttIds.length; i += 1) {
            if (!originalCttSet.has(currentCttIds[i])) return true;
        }
        
        return false;
    }

    async function handleSave() {
        if (!formSite?.siteName?.trim()) {
            toast.error(t("Name is required."));
            return;
        }

        if (!company.id) {
            toast.error(t("Company id is required to save site details."));
            return;
        }

        if (typeof updateSpinner === "function") {
            updateSpinner(true);
        }

        const payload = { ...formSite, companyId: company.id, cttIds: selectedCttIds };
        const endpoint = `/Company/${company.id}/Site`;
        const resp = formSite?.siteId > 0
            ? await RightTimeApi.putAsync(endpoint, payload)
            : await RightTimeApi.postAsync(endpoint, payload);

        if (typeof updateSpinner === "function") {
            updateSpinner(false);
        }

        if (resp?.ok) {
            const updated = resp.jsonData || payload;
            setSite(updated);
            setFormSite(updated);
            setOriginalSite(updated);
            toast.success(t("{entityName} saved successfully.", "{entityName} saved successfully.", { entityName: company.subEntityTypeName }));
            if (typeof onClose === "function") {
                onClose(updated);
            }
        } else {
            toast.error(t("Failed to save {entityName} details.", "Failed to save {entityName} details.", { entityName: String(company.subEntityTypeName || "site").toLowerCase() }));
        }
    }

    if (!isOpen) return null;
    if (isLoading && !site) return null;
    if (!site || !formSite) return null;

    const isNewSite = !formSite?.siteId;
    const canSave = Boolean(formSite?.siteName?.trim()) && (isNewSite || isChanged());

    return (
        <Modal
            isOpen={isOpen}
            title={
                site?.siteId > 0
                    ? t("Edit {entityName} ({id})", "Edit {entityName} ({id})", { entityName: company?.subEntityTypeName || "Site", id: site.siteId })
                    : t("Add {entityName}", "Add {entityName}", { entityName: company?.subEntityTypeName || "Site" })
            }
            onClose={onClose}
            className="editSiteModal"
            footer={
                <div className="footerRight">
                    <button
                        className="btn btn-primary okButton"
                        onClick={handleSave}
                        disabled={!canSave}
                    >
                        {t("Save")}
                    </button>
                </div>
            }
        >
            <div className="tableWrapper dimensionTable siteFormTable">
                <table className="formTable">
                    <tbody>
                        <tr>
                            <th className="label">{t("Name")}</th>
                            <td>
                                <input
                                    id="siteName"
                                    type="text"
                                    value={formSite.siteName || ""}
                                    onChange={handleNameChange}
                                    onKeyDown={handleNameKeyDown}
                                />
                            </td>
                        </tr>
                        <tr>
                            <th className="label">{t("Alias")}</th>
                            <td>
                                <input
                                    id="siteAlias"
                                    type="text"
                                    value={formSite.siteAlias || ""}
                                    onChange={handleAliasChange}
                                    onKeyDown={handleNameKeyDown}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {Array.isArray(jobs) && jobs.length > 0 ? (
                <div className="jobsTable tableWrapper dimensionTable scrollable" style={{ marginTop: "16px" }}>
                    <table>
                        <caption>
                          <span className="title">{t("Jobs")}</span>
                        </caption>
                        <thead>
                            <tr>
                                <th>{t("Job Name")}</th>
                                <th>{t("Available")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((job) => (
                                <tr key={job.key||job.id}>
                                    <td>{job.value || job.name || ""}</td>
                                    <td style={{ textAlign: "center" }}>
                                        {Array.isArray(formSite.jobs) && formSite.jobs.includes(job.id)
                                            ? (
                                                <MdCheckBox
                                                    className="actionIcon"
                                                    style={{
                                                        cursor: "pointer",
                                                        color: "var(--dim-table-caption-bg-color)"
                                                    }}
                                                    title={t("Available")}
                                                    aria-label={t("Mark as not available")}
                                                    onClick={() => toggleJobAvailability(job.id)}
                                                />
                                            )
                                            : (
                                                <MdCheckBoxOutlineBlank
                                                    className="actionIcon"
                                                    style={{
                                                        cursor: "pointer",
                                                        color: "var(--dim-table-caption-bg-color)"
                                                    }}
                                                    title={t("Not available")}
                                                    aria-label={t("Mark as available")}
                                                    onClick={() => toggleJobAvailability(job.id)}
                                                />
                                            )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
            {Array.isArray(companyTestTypes) && companyTestTypes.length > 0 ? (
                <div className="tableWrapper dimensionTable scrollable" style={{ marginTop: "16px" }}>
                    <table>
                        <caption>
                          <span className="title">{t("Company Test Types")}</span>
                        </caption>
                        <thead>
                            <tr>
                                <th>{t("Test Type")}</th>
                                <th>{t("Available")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {companyTestTypes.map((ctt) => (
                                <tr key={ctt.id}>
                                    <td>{ctt.name || ""}</td>
                                    <td style={{ textAlign: "center" }}>
                                        {selectedCttIds.includes(ctt.id)
                                            ? (
                                                <MdCheckBox
                                                    className="actionIcon"
                                                    style={{
                                                        cursor: "pointer",
                                                        color: "var(--dim-table-caption-bg-color)"
                                                    }}
                                                    title={t("Available")}
                                                    aria-label={t("Mark as not available")}
                                                    onClick={() => toggleCttAvailability(ctt.id)}
                                                />
                                            )
                                            : (
                                                <MdCheckBoxOutlineBlank
                                                    className="actionIcon"
                                                    style={{
                                                        cursor: "pointer",
                                                        color: "var(--dim-table-caption-bg-color)"
                                                    }}
                                                    title={t("Not available")}
                                                    aria-label={t("Mark as available")}
                                                    onClick={() => toggleCttAvailability(ctt.id)}
                                                />
                                            )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </Modal>
    );
}


